import React, { useEffect, useState } from 'react';
import {
  ArrowRightOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HomeOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  UserAddOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Button, Modal, Tabs, notification } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import {
  auth,
  createUserWithEmailAndPassword,
  database,
  get,
  ref,
  set,
  signInWithEmailAndPassword
} from './db/firebase';
import { loginCashierByEmail } from '../../utils/cashierSession';
import './AuthExperience.css';

const defaultRegisterState = {
  username: '',
  email: '',
  phone: '',
  facilityId: '',
  password: '',
  confirm: '',
  agree: false
};

const AuthExperience = ({ defaultTab = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginErrors, setLoginErrors] = useState({});
  const [loginShowPassword, setLoginShowPassword] = useState(false);

  const [registerData, setRegisterData] = useState(defaultRegisterState);
  const [registerErrors, setRegisterErrors] = useState({});
  const [registerShowPassword, setRegisterShowPassword] = useState(false);
  const [registerShowConfirm, setRegisterShowConfirm] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [secondaryPassword, setSecondaryPassword] = useState('');
  const [showSecondaryPassword, setShowSecondaryPassword] = useState(false);
  const [secondaryError, setSecondaryError] = useState('');
  const [userData, setUserData] = useState(null);

  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const openNotification = (type, message) => {
    api[type]({
      message,
      placement: 'bottomRight',
      style: {
        background: 'rgba(6, 9, 21, 0.8)',
        color: '#fff',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: 16,
        boxShadow: '0 4px 25px rgba(2, 6, 23, 0.4)'
      }
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    const target = key === 'login' ? '/login' : '/register';
    if (location.pathname !== target) {
      navigate(target, { replace: true });
    }
  };

  const validateLogin = () => {
    const errors = {};
    if (!loginData.email) errors.email = 'Email required';
    else if (!/\S+@\S+\.\S+/.test(loginData.email)) errors.email = 'Use a valid email';
    if (!loginData.password) errors.password = 'Password required';
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRegister = () => {
    const errors = {};
    if (!registerData.username) errors.username = 'Name required';
    if (!registerData.email) errors.email = 'Email required';
    else if (!/\S+@\S+\.\S+/.test(registerData.email)) errors.email = 'Use a valid email';
    if (!registerData.phone) errors.phone = 'Phone required';
    if (!registerData.facilityId) errors.facilityId = 'Facility ID required';
    if (!registerData.password) errors.password = 'Password required';
    else if (registerData.password.length < 6) errors.password = 'Min 6 characters';
    if (!registerData.confirm) errors.confirm = 'Confirm your password';
    else if (registerData.password !== registerData.confirm) errors.confirm = 'Passwords must match';
    if (!registerData.agree) errors.agree = 'Please accept the terms';
    setRegisterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLoginInputChange = (event) => {
    const { name, value } = event.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegisterInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setRegisterData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (!validateLogin()) return;

    try {
      await signInWithEmailAndPassword(auth, loginData.email, loginData.password);

      const userSnapshot = await get(ref(database, 'users'));
      let foundUser = null;
      let userFacilityId = null;

      if (userSnapshot.exists()) {
        const facilities = userSnapshot.val();

        if (facilities.me && facilities.me.email === loginData.email) {
          foundUser = facilities.me;
          userFacilityId = facilities.me.facilityid;
        } else {
          for (const fid in facilities) {
            if (fid === 'me') continue;
            const users = facilities[fid];
            for (const uid in users) {
              if (users[uid].email === loginData.email) {
                foundUser = users[uid];
                userFacilityId = fid;
                break;
              }
            }
            if (foundUser) break;
          }
        }
      }

      if (!foundUser) {
        const pendingSnapshot = await get(ref(database, 'pending_users'));
        if (pendingSnapshot.exists()) {
          const facilities = pendingSnapshot.val();
          for (const pendingFacilityId in facilities) {
            const users = facilities[pendingFacilityId];
            const isPending = Object.values(users).some((u) => u.email === loginData.email);
            if (isPending) {
              openNotification('error', 'Account pending approval. Please contact support.');
              await auth.signOut();
              return;
            }
          }
        }

        openNotification('error', 'We could not locate your account. Please contact support.');
        await auth.signOut();
        return;
      }

      if (foundUser.accountHeld) {
        openNotification('error', 'Your account has been suspended. Please contact support.');
        await auth.signOut();
        return;
      }

      setUserData(foundUser);

      if (foundUser.role === 'superadmin') {
        setIsModalVisible(true);
        return;
      }

      let destination = null;
      if (foundUser.role === 'admin') {
        destination = `/${userFacilityId}/admin`;
      } else if (foundUser.role === 'cashier') {
        destination = `/${userFacilityId}/user`;
      }

      if (destination) {
        openNotification('success', 'Login successful!');
        navigate(destination);
        return;
      }

      openNotification('error', 'Unsupported user role. Please contact support.');
      await auth.signOut();
    } catch (err) {
      try {
        const { facilityId } = await loginCashierByEmail(loginData.email, loginData.password);
        openNotification('success', 'Login successful!');
        navigate(`/${facilityId}/cashier`);
        return;
      } catch (cashierError) {
        console.error('Cashier email login failed', cashierError);
      }

      const invalidCodes = ['auth/wrong-password', 'auth/user-not-found', 'auth/invalid-credential'];
      if (invalidCodes.includes(err.code)) {
        openNotification('error', 'Wrong email or password.');
      } else {
        openNotification('error', 'Unable to sign in right now. Please try again.');
      }
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    if (!validateRegister()) return;

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, registerData.email, registerData.password);
      const user = userCredential.user;

      await set(ref(database, `pending_users/${registerData.facilityId}/${user.uid}`), {
        username: registerData.username,
        email: registerData.email,
        phone: registerData.phone,
        facilityId: registerData.facilityId,
        role: 'admin',
        createdAt: new Date().toISOString()
      });

  openNotification('success', 'Registration submitted! Awaiting approval.');
  setRegisterData(defaultRegisterState);
  setActiveTab('login');
  navigate('/login');
    } catch (err) {
      openNotification('error', `Registration failed: ${err.message}`);
    }
  };

  const handleSecondaryPasswordSubmit = async () => {
    if (!secondaryPassword) {
      setSecondaryError('Please input the secondary password!');
      return;
    }

    try {
      const isMatch = await bcrypt.compare(secondaryPassword, userData.secondaryPassword);

      if (isMatch) {
        setIsModalVisible(false);
        setSecondaryPassword('');
        setSecondaryError('');
        navigate('/superadmin', { replace: true });
        openNotification('success', 'Superadmin login successful!');
      } else {
        setSecondaryError('Incorrect secondary password.');
      }
    } catch (err) {
      console.error('Error verifying secondary password:', err);
      setSecondaryError('Error verifying secondary password.');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSecondaryPassword('');
    setSecondaryError('');
    auth.signOut();
    openNotification('info', 'Superadmin login cancelled.');
  };

  const loginForm = (
    <form className="auth-form" onSubmit={handleLoginSubmit}>
      <div className="auth-field">
        <label>Email</label>
        <div className="auth-input">
          <UserOutlined />
          <input
            name="email"
            value={loginData.email}
            onChange={handleLoginInputChange}
            placeholder="Email"
            autoComplete="email"
          />
        </div>
        {loginErrors.email && <span className="auth-error">{loginErrors.email}</span>}
      </div>
      <div className="auth-field">
        <label>Password</label>
        <div className="auth-input">
          <LockOutlined />
          <input
            name="password"
            type={loginShowPassword ? 'text' : 'password'}
            value={loginData.password}
            onChange={handleLoginInputChange}
            placeholder="Password"
            autoComplete="current-password"
          />
          {loginShowPassword ? (
            <EyeInvisibleOutlined onClick={() => setLoginShowPassword(false)} />
          ) : (
            <EyeOutlined onClick={() => setLoginShowPassword(true)} />
          )}
        </div>
        {loginErrors.password && <span className="auth-error">{loginErrors.password}</span>}
      </div>
      <div className="auth-form__row">
        <label className="auth-checkbox">
          <input type="checkbox" />
          <span>Remember me</span>
        </label>
        <button type="button" className="link-button">Forgot password?</button>
      </div>
      <Button type="primary" htmlType="submit" block className="auth-submit">
        Log in
      </Button>
      <p className="auth-hint">
        Need an account? <button type="button" className="link-button" onClick={() => handleTabChange('register')}>Register here</button>
      </p>
    </form>
  );

  const registerForm = (
    <form className="auth-form" onSubmit={handleRegisterSubmit}>
      <div className="auth-grid">
        <div className="auth-field">
          <label>Full name</label>
          <div className="auth-input">
            <UserOutlined />
            <input
              name="username"
              value={registerData.username}
              onChange={handleRegisterInputChange}
              placeholder="Full name"
            />
          </div>
          {registerErrors.username && <span className="auth-error">{registerErrors.username}</span>}
        </div>
        <div className="auth-field">
          <label>Phone</label>
          <div className="auth-input">
            <PhoneOutlined />
            <input
              name="phone"
              value={registerData.phone}
              onChange={handleRegisterInputChange}
              placeholder="Phone number"
            />
          </div>
          {registerErrors.phone && <span className="auth-error">{registerErrors.phone}</span>}
        </div>
      </div>
      <div className="auth-grid">
        <div className="auth-field">
          <label>Email</label>
          <div className="auth-input">
            <MailOutlined />
            <input
              name="email"
              value={registerData.email}
              onChange={handleRegisterInputChange}
              placeholder="Email"
              autoComplete="email"
            />
          </div>
          {registerErrors.email && <span className="auth-error">{registerErrors.email}</span>}
        </div>
        <div className="auth-field">
          <label>Facility ID</label>
          <div className="auth-input">
            <HomeOutlined />
            <input
              name="facilityId"
              value={registerData.facilityId}
              onChange={handleRegisterInputChange}
              placeholder="Shop or facility ID"
            />
          </div>
          {registerErrors.facilityId && <span className="auth-error">{registerErrors.facilityId}</span>}
        </div>
      </div>
      <div className="auth-grid">
        <div className="auth-field">
          <label>Password</label>
          <div className="auth-input">
            <LockOutlined />
            <input
              name="password"
              type={registerShowPassword ? 'text' : 'password'}
              value={registerData.password}
              onChange={handleRegisterInputChange}
              placeholder="Password"
              autoComplete="new-password"
            />
            {registerShowPassword ? (
              <EyeInvisibleOutlined style={{marginLeft: "-30px"}} onClick={() => setRegisterShowPassword(false)} />
            ) : (
              <EyeOutlined style={{marginLeft: "-30px"}} onClick={() => setRegisterShowPassword(true)} />
            )}
          </div>
          {registerErrors.password && <span className="auth-error">{registerErrors.password}</span>}
        </div>
        <div className="auth-field">
          <label>Confirm</label>
          <div className="auth-input">
            <LockOutlined />
            <input
              name="confirm"
              type={registerShowConfirm ? 'text' : 'password'}
              value={registerData.confirm}
              onChange={handleRegisterInputChange}
              placeholder="Confirm password"
              autoComplete="new-password"
            />
            {registerShowConfirm ? (
              <EyeInvisibleOutlined style={{marginLeft: "-30px"}} onClick={() => setRegisterShowConfirm(false)} />
            ) : (
              <EyeOutlined  style={{marginLeft: "-30px"}} onClick={() => setRegisterShowConfirm(true)} />
            )}
          </div>
          {registerErrors.confirm && <span className="auth-error">{registerErrors.confirm}</span>}
        </div>
      </div>
      <label className={`auth-checkbox ${registerErrors.agree ? 'has-error' : ''}`}>
        <input
          type="checkbox"
          name="agree"
          checked={registerData.agree}
          onChange={handleRegisterInputChange}
        />
        <span>
          I agree to the <a href="#terms" target="_blank" rel="noreferrer">Terms of Service</a>
        </span>
      </label>
      {registerErrors.agree && <span className="auth-error">{registerErrors.agree}</span>}
      <Button type="primary" htmlType="submit" block className="auth-submit" disabled={!registerData.agree}>
        Submit for approval
      </Button>
      <p className="auth-hint">
        Already onboarded? <button type="button" className="link-button" onClick={() => handleTabChange('login')}>Log in</button>
      </p>
    </form>
  );

  const items = [
    {
      key: 'login',
      label: 'Log in',
      children: loginForm
    },
    {
      key: 'register',
      label: 'Register',
      children: registerForm
    }
  ];

  return (
    <div className="auth-page">
      {contextHolder}
      <div className="auth-page__glow" />
      <div className="auth-shell">
        <div className="auth-copy">
          <span className="auth-copy__kicker">Welcome back</span>
          <h1 style={ { textWrap: "balance"}}>Take control of your operations</h1>
          <p>Access your Store management system and stay in control of your business operations.</p>
          <ul>
            <li><ArrowRightOutlined /> Manage inventory in real-time</li>
            <li><ArrowRightOutlined /> Track sales and transactions</li>
            <li><ArrowRightOutlined /> Secure multi-role access</li>
          </ul>
        </div>
        <div className="auth-card veil-card">
          <div className="auth-card__header">
            <UserAddOutlined />
            <div>
              <h3>Access your workspace</h3>
              <p>Log back in or request approval.</p>
            </div>
          </div>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={items}
            className="auth-tabs"
          />
        </div>
      </div>

      <Modal
        title="Superadmin Secondary Password"
        open={isModalVisible}
        onOk={handleSecondaryPasswordSubmit}
        onCancel={handleModalCancel}
        okButtonProps={{ style: { background: '#5b7cfa', borderColor: '#5b7cfa' } }}
        cancelButtonProps={{ style: { borderColor: 'rgba(255,255,255,0.2)' } }}
        style={{ top: 20 }}
        styles={{
          body: { background: 'rgba(6, 9, 21, 0.9)', color: '#fff' },
          header: { background: 'rgba(6, 9, 21, 0.9)', color: '#fff' }
        }}
        className="superadmin-modal"
      >
        <div className="auth-field">
          <label>Enter Secondary Password</label>
          <div className="auth-input">
            <LockOutlined />
            <input
              type={showSecondaryPassword ? 'text' : 'password'}
              value={secondaryPassword}
              onChange={(e) => setSecondaryPassword(e.target.value)}
              placeholder="Secondary Password"
            />
            {showSecondaryPassword ? (
              <EyeInvisibleOutlined onClick={() => setShowSecondaryPassword(false)} />
            ) : (
              <EyeOutlined onClick={() => setShowSecondaryPassword(true)} />
            )}
          </div>
          {secondaryError && <span className="auth-error">{secondaryError}</span>}
        </div>
      </Modal>
    </div>
  );
};

export default AuthExperience;
